import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  FileText, 
  MessageSquare, 
  BarChart3, 
  Settings,
  LogOut,
  Plus,
  Trash2,
  Edit,
  Eye,
  Loader2
} from 'lucide-react';

const MLM_LOGO = "https://customer-assets.emergentagent.com/job_5389659f-dea1-4240-af6c-b14886122f6e/artifacts/6yszwf33_Digital%20Logo%20with%20Framed%20Letters.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const fetchAdmin = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Unauthorized');
        const data = await response.json();
        setAdmin(data);
      } catch (error) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    fetchAdmin();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-mlm" />
      </div>
    );
  }

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/contacts', label: 'Contacts', icon: MessageSquare },
    { path: '/admin/blog', label: 'Blog Posts', icon: FileText },
    { path: '/admin/impact', label: 'Impact Metrics', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="admin-dashboard">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-[calc(100vh-80px)] bg-white border-r border-slate-200 p-6 fixed left-0 top-20">
          <img src={MLM_LOGO} alt="#MyLifeMatters" className="h-10 w-auto mb-8" />
          
          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  location.pathname === item.path
                    ? 'bg-purple-100 text-primary-mlm'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-6 left-6 right-6">
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <p className="text-sm font-medium text-slate-900">{admin?.name}</p>
              <p className="text-xs text-slate-500">{admin?.email}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start gap-2 text-slate-600"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 p-8">
          <Routes>
            <Route path="dashboard" element={<DashboardOverview />} />
            <Route path="contacts" element={<ContactsManager />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="impact" element={<ImpactManager />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({ contacts: 0, posts: 0, metrics: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      const token = localStorage.getItem('admin_token');
      try {
        const [contactsRes, postsRes, metricsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_URL}/api/blog?published_only=false`),
          fetch(`${API_URL}/api/impact`),
        ]);
        
        const contacts = contactsRes.ok ? await contactsRes.json() : [];
        const posts = postsRes.ok ? await postsRes.json() : [];
        const metrics = metricsRes.ok ? await metricsRes.json() : [];
        
        setStats({
          contacts: contacts.length,
          posts: posts.length,
          metrics: metrics.length,
        });
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div>
      <h1 className="text-3xl font-heading font-bold text-slate-900 mb-8">Dashboard</h1>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          { label: 'Contact Submissions', value: stats.contacts, icon: MessageSquare, color: 'bg-blue-500' },
          { label: 'Blog Posts', value: stats.posts, icon: FileText, color: 'bg-emerald-500' },
          { label: 'Impact Metrics', value: stats.metrics, icon: BarChart3, color: 'bg-purple-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-6 border border-slate-200">
            <div className={`w-12 h-12 ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
              <stat.icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-3xl font-heading font-bold text-slate-900">{stat.value}</p>
            <p className="text-slate-600">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Contacts Manager
const ContactsManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/contacts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Contact deleted');
        fetchContacts();
      }
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-heading font-bold text-slate-900 mb-8">Contact Submissions</h1>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-mlm" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No contact submissions yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact) => (
            <div key={contact.id} className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{contact.name}</h3>
                  <p className="text-sm text-slate-500">{contact.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-purple-100 text-primary-mlm text-xs font-medium rounded-full">
                    {contact.form_type}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteContact(contact.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {contact.phone && <p className="text-sm text-slate-600 mb-2">Phone: {contact.phone}</p>}
              {contact.company && <p className="text-sm text-slate-600 mb-2">Company: {contact.company}</p>}
              <p className="text-slate-700 bg-slate-50 rounded-lg p-4">{contact.message}</p>
              <p className="text-xs text-slate-400 mt-4">
                {new Date(contact.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Blog Manager
const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Insights',
    image_url: '',
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog?published_only=false`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');
    
    try {
      const url = editingPost 
        ? `${API_URL}/api/admin/blog/${editingPost.id}`
        : `${API_URL}/api/admin/blog`;
      
      const response = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        setShowForm(false);
        setEditingPost(null);
        setFormData({ title: '', slug: '', excerpt: '', content: '', category: 'Insights', image_url: '' });
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/blog/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        toast.success('Post deleted');
        fetchPosts();
      }
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image_url: post.image_url || '',
    });
    setShowForm(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-heading font-bold text-slate-900">Blog Posts</h1>
        <Button
          onClick={() => {
            setEditingPost(null);
            setFormData({ title: '', slug: '', excerpt: '', content: '', category: 'Insights', image_url: '' });
            setShowForm(true);
          }}
          className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
          <h2 className="text-xl font-semibold mb-6">{editingPost ? 'Edit Post' : 'New Post'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
              <div>
                <Label>Image URL</Label>
                <Input
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Excerpt</Label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={2}
                required
              />
            </div>
            <div>
              <Label>Content (HTML)</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                required
              />
            </div>
            <div className="flex gap-4">
              <Button type="submit" className="bg-primary-mlm hover:bg-[#5b21b6] text-white rounded-full">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-mlm" />
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">No blog posts yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-xl p-6 border border-slate-200 flex items-center gap-6">
              {post.image_url && (
                <img src={post.image_url} alt="" className="w-20 h-20 rounded-lg object-cover" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">{post.title}</h3>
                <p className="text-sm text-slate-500">{post.excerpt}</p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-xs text-slate-400">{post.category}</span>
                  <span className={`text-xs ${post.published ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => startEdit(post)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/insights/${post.slug}`} target="_blank">
                    <Eye className="w-4 h-4" />
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deletePost(post.id)} className="text-red-500">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Impact Metrics Manager
const ImpactManager = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMetric, setEditingMetric] = useState(null);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/impact`);
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      toast.error('Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const updateMetric = async (id, value) => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await fetch(`${API_URL}/api/admin/impact/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ value: parseInt(value) }),
      });
      if (response.ok) {
        toast.success('Metric updated');
        setEditingMetric(null);
        fetchMetrics();
      }
    } catch (error) {
      toast.error('Failed to update metric');
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-heading font-bold text-slate-900 mb-8">Impact Metrics</h1>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-mlm" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric) => (
            <div key={metric.id} className="bg-white rounded-xl p-6 border border-slate-200">
              <p className="text-sm text-slate-500 mb-2">{metric.label}</p>
              {editingMetric === metric.id ? (
                <div className="flex gap-2">
                  <Input
                    type="number"
                    defaultValue={metric.value}
                    onBlur={(e) => updateMetric(metric.id, e.target.value)}
                    autoFocus
                    className="w-24"
                  />
                  <Button variant="outline" size="sm" onClick={() => setEditingMetric(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <p className="text-3xl font-heading font-bold text-slate-900">{metric.value}</p>
                  <Button variant="ghost" size="sm" onClick={() => setEditingMetric(metric.id)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
